import type { DemUnifiedOrder } from '../../types/dem';
import { useMaxWidth } from '../../hooks/useMaxWidth';
import { canMarkOrderChecked, orderIsChecked, orderNeedsCheck } from '../../lib/demOrderChecked';
import { poOrderStatusTone } from '../../lib/demPoOrders';
import { sourceChipAbbr, sourceChipClass } from '../../lib/demUnifiedOrders';

type UnifiedOrderRowProps = {
  order: DemUnifiedOrder;
  selected: boolean;
  checkBusy?: boolean;
  /** Customer or line item blocked (master or cust×item list). */
  blockWarning?: boolean;
  onSelect: (order: DemUnifiedOrder) => void;
  onOpen: (order: DemUnifiedOrder) => void;
  onMarkChecked: (order: DemUnifiedOrder) => void;
  onUnmarkChecked: (order: DemUnifiedOrder) => void;
};

/** Row background tone — do not treat "Checked?" as pending (avoids red wash). */
function listRowTone(order: DemUnifiedOrder): string {
  return poOrderStatusTone(order.status);
}

/** Status pill tone — "Checked?" still uses pending styling on the badge only. */
function listStatusTone(order: DemUnifiedOrder): string {
  if (orderNeedsCheck(order)) return 'pending';
  return poOrderStatusTone(order.status);
}

function formatOrderAmountParts(amount: number): { ccy: string; num: string } | null {
  if (!Number.isFinite(amount)) return null;
  const abs = Math.abs(amount);
  const num = abs.toLocaleString('en-SG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = amount < 0 ? '−' : '';
  return { ccy: `${sign}$`, num };
}

export function UnifiedOrderRow({
  order,
  selected,
  checkBusy,
  blockWarning = false,
  onSelect,
  onOpen,
  onMarkChecked,
}: UnifiedOrderRowProps) {
  const rowTone = listRowTone(order);
  const statusTone = listStatusTone(order);
  const isMobile = useMaxWidth(900);
  const abbr = sourceChipAbbr(order.source);
  const chipClass = sourceChipClass(order.source);
  const needsCheck = canMarkOrderChecked(order);
  const checked = orderIsChecked(order);
  const remarks =
    order.source === 'CallIn' ? '—' : order.remarks?.trim() || '—';
  const amountParts = formatOrderAmountParts(order.total_amount);

  return (
    <div
      role="button"
      tabIndex={0}
      className={`dem-po-order-row dem-po-order-row--${rowTone}${selected ? ' dem-po-order-row--selected' : ''}`}
      onClick={() => {
        onSelect(order);
        if (isMobile) onOpen(order);
      }}
      onDoubleClick={() => onOpen(order)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(order);
          if (isMobile) onOpen(order);
        }
      }}
    >
      <span className="dem-po-order-row__id dem-unified-order-row__id">
        <span className="dem-unified-order-row__id-line">
          <button
            type="button"
            className="dem-po-order-row__id-btn"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(order);
              onOpen(order);
            }}
          >
            {order.order_id}
          </button>
          {blockWarning ? (
            <span
              className="dem-unified-order-row__block-warn"
              title="Customer or item blocked — open order for details"
              aria-label="Customer or item blocked"
            >
              ⚠
            </span>
          ) : null}
        </span>
        <span
          className={`dem-order-source-chip dem-order-source-chip--${chipClass}`}
          title={abbr}
        >
          {abbr}
        </span>
      </span>
      <span className="dem-po-order-row__customer dem-unified-order-row__customer">
        <span className="dem-unified-order-row__customer-name">
          {order.customer_name?.trim() || '—'}
        </span>
        {order.customer_code?.trim() ? (
          <span className="dem-unified-order-row__customer-code">{order.customer_code.trim()}</span>
        ) : null}
      </span>
      <span className="dem-po-order-row__time">{order.created_at_display}</span>
      <span className="dem-po-order-row__date">{order.delivery_date_display}</span>
      <span className="dem-po-order-row__po">{order.ref_no}</span>
      <span className="dem-po-order-row__remarks" title={remarks !== '—' ? remarks : undefined}>
        {remarks}
      </span>
      <span className="dem-po-order-row__amount">
        {amountParts ? (
          <>
            <span className="dem-po-order-row__amount-ccy">{amountParts.ccy}</span>
            <span className="dem-po-order-row__amount-num">{amountParts.num}</span>
          </>
        ) : (
          '—'
        )}
      </span>
      <span
        className={`dem-web-order-status dem-web-order-status--${statusTone} dem-unified-order-row__status`}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {needsCheck ? (
          <button
            type="button"
            className="dem-order-checked__btn dem-order-checked__btn--mark"
            disabled={checkBusy}
            onClick={(e) => {
              e.stopPropagation();
              onMarkChecked(order);
            }}
          >
            {checkBusy ? '…' : 'Checked?'}
          </button>
        ) : (
          <span
            title={
              checked && order.status === 'Processed'
                ? `${order.checked_by || 'Checked'}${
                    order.checked_at_display && order.checked_at_display !== '—'
                      ? ` · ${order.checked_at_display}`
                      : ''
                  }`
                : undefined
            }
          >
            {order.status}
          </span>
        )}
      </span>
      <span className="dem-po-order-row__user dem-unified-order-row__by">
        <span className="dem-unified-order-row__by-created">{order.created_by || '—'}</span>
        <span className="dem-unified-order-row__by-exported">{order.exported_by || '—'}</span>
      </span>
    </div>
  );
}
